/**
 * DSD 프리뷰 렌더러용 셀 드래그 선택 훅.
 *
 * IXD 표와 동일한 공통 셀 선택 훅(useTableCellSelection)을 컨테이너 위임 방식으로 사용한다.
 * 따라서 DSD 셀에는 셀별 핸들러를 달 필요 없이 `id`(= dsdCellDomId) + `data-cell-id` 만
 * 있으면 자동으로 드래그 선택이 동작한다.
 *
 * DSD 는 자체 방향키 navigation(useDsdCellNavigation)을 가지므로 공통 훅의 방향키 이동은 끈다.
 */

import type { RefObject } from "react";
import {
  MULTI_SELECT_CELL_BORDER_CSS,
  useTableCellSelection,
} from "@/hooks/use-table-cell-selection";

export const DSD_SELECTION_STYLE_ID = "dsd-cell-selection-style";

/**
 * DSD 셀의 고유 DOM id(선택 키).
 * IXD 등 같은 화면의 다른 표 셀 id 와 충돌하지 않도록 prefix 를 붙인다.
 */
export const dsdCellDomId = (dsdId: string): string => `dsd-cell-${dsdId}`;

/**
 * 다중 선택으로 간주하는 최소 셀 수. 단일 클릭(=size 1)은 navigation focus(ring)가
 * 담당하므로 검은 테두리 강조도, DSD→IXD 다중 강조도 띄우지 않는다. 드래그로 2개 이상
 * 선택될 때만 다중 선택으로 본다.
 */
export const DSD_MULTI_SELECT_MIN = 2;

interface UseDsdCellSelectionOptions {
  /**
   * 선택 상태 변경 콜백. `isMulti`(셀 수 >= DSD_MULTI_SELECT_MIN)와 그때의 dsdId 목록을 준다.
   * 단일 선택(클릭)일 때는 isMulti=false, dsdIds=[] 로 보고된다(매칭 IXD 다중 강조 OFF).
   */
  onSelectionStateChange?: (state: {
    isMulti: boolean;
    dsdIds: string[];
  }) => void;
}

export function useDsdCellSelection(
  containerRef: RefObject<HTMLDivElement | null>,
  options?: UseDsdCellSelectionOptions,
) {
  return useTableCellSelection(containerRef, {
    styleId: DSD_SELECTION_STYLE_ID,
    autoBindContainer: true,
    // 헤더(th) 선택과 엑셀식 그리드 범위 선택은 이 화면에서 쓰지 않는다 —
    // 기본값(td 전용 + rect 겹침 규칙)을 유지해 기존 드래그 동작을 그대로 둔다.
    enableArrowNavigation: false,
    // 셀별 검은 테두리(공유 변 겹침 보정) — match 배경색(green/amber/red)을 보존한다.
    selectionCss: MULTI_SELECT_CELL_BORDER_CSS,
    // 단일 클릭(size 1)에는 검은 테두리를 띄우지 않는다(navigation ring 만 사용).
    minSelectionToStyle: DSD_MULTI_SELECT_MIN,
    onSelectionChange: options?.onSelectionStateChange
      ? (uniqueIds, entries) => {
          const isMulti = uniqueIds.length >= DSD_MULTI_SELECT_MIN;
          options.onSelectionStateChange?.({
            isMulti,
            dsdIds: isMulti ? entries.map((e) => e.cellId) : [],
          });
        }
      : undefined,
  });
}
